import { useEffect, useId, useRef } from 'react'
import { Icon } from '@iconify/react'
import { COUNTRY_FLAGS, getDecisiveBossScores, getStageBoss, type GameStateV2 } from '../game'
import { ClubCrest } from './ClubCrest'
import './BossSeriesResultDialog.css'

interface BossSeriesResultDialogProps {
  state: GameStateV2
  onContinue: () => void
}

export function BossSeriesResultDialog({ state, onContinue }: BossSeriesResultDialogProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLElement>(null)
  const boss = getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind)
  const results = getDecisiveBossScores(boss, state.boss?.scores ?? [])
  const wins = results.filter((result) => result === 'win').length
  const losses = results.length - wins
  const completed = wins >= boss.requiredWins
  const remainingRounds = boss.rounds.slice(results.length)
  const finalPlayedRound = boss.rounds[results.length - 1] ?? 'esta ronda'
  const resolutionCopy = remainingRounds.length === 0
    ? 'Todas las rondas ya están resueltas.'
    : completed
      ? `El objetivo quedó asegurado en ${finalPlayedRound}; ${remainingRounds.length === 1 ? 'la ronda restante ya no era necesaria' : 'las rondas restantes ya no eran necesarias'}.`
      : `El objetivo quedó fuera de alcance en ${finalPlayedRound}; ${remainingRounds.length === 1 ? 'la ronda restante no se disputó' : 'las rondas restantes no se disputaron'}.`
  const isInternational = state.season === 5 && state.finaleKind === 'world-cup'
  const teamName = isInternational ? state.player.country : state.club

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus())
    const trap = (event: KeyboardEvent) => {
      if (event.key === 'Escape') event.preventDefault()
      if (event.key === 'Tab') { event.preventDefault(); dialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus() }
    }
    document.addEventListener('keydown', trap)
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', trap) }
  }, [])

  return (
    <div className="dialog-backdrop boss-result-backdrop">
      <section ref={dialogRef} className={`boss-result${completed ? ' is-complete' : ' is-eliminated'}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="boss-result__header">
          <div><p className="eyebrow">Resultado del tramo decisivo</p><h2 id={titleId}>{boss.name}</h2><p>{resolutionCopy} Este es el balance definitivo de la etapa.</p></div>
          <span className="boss-result__verdict"><Icon icon={completed ? 'mdi:trophy-outline' : 'mdi:whistle-outline'} />{completed ? 'Objetivo cumplido' : 'Objetivo fallado'}</span>
        </header>

        <section className="boss-result__scoreboard" aria-label={`${teamName}: ${wins} ${wins === 1 ? 'victoria' : 'victorias'} y ${losses} ${losses === 1 ? 'derrota' : 'derrotas'}`}>
          <div className="boss-result__team">
            {isInternational ? <img src={COUNTRY_FLAGS[state.player.country]} alt="" /> : <ClubCrest name={state.club} size="large" />}
            <span><small>{isInternational ? 'Tu selección' : 'Tu equipo'}</small><strong>{teamName}</strong></span>
          </div>
          <div className="boss-result__score"><strong>{wins}</strong><i>—</i><strong>{losses}</strong><small>Balance final</small></div>
          <div className="boss-result__target"><small>Objetivo</small><strong>{boss.requiredWins} victorias</strong></div>
        </section>

        <ol className="boss-result__rounds" aria-label="Estado de las rondas del tramo decisivo">
          {boss.rounds.slice(0, results.length).map((round, index) => {
            const result = results[index]
            const won = result === 'win'
            return <li className={won ? 'is-win' : 'is-loss'} key={round}>
              <span className="boss-result__round-number">{String(index + 1).padStart(2, '0')}</span>
              <div><small>Ronda {index + 1}</small><strong>{round}</strong></div>
              <span className="boss-result__round-outcome"><Icon icon={won ? 'mdi:check-bold' : 'mdi:close-thick'} />{won ? 'Victoria' : 'Derrota'}</span>
            </li>
          })}
          {remainingRounds.map((round, offset) => {
            const index = results.length + offset
            return <li className="is-skipped" key={round}>
              <span className="boss-result__round-number">{String(index + 1).padStart(2, '0')}</span>
              <div><small>Ronda {index + 1}</small><strong>{round}</strong></div>
              <span className="boss-result__round-outcome"><Icon icon="mdi:minus" />{completed ? 'No necesaria' : 'Sin disputar'}</span>
            </li>
          })}
        </ol>

        <footer className="boss-result__footer">
          <p>{completed ? boss.success : boss.failure}</p>
          <button className="button button--primary" type="button" onClick={onContinue}>Continuar <Icon icon="mdi:arrow-right" /></button>
        </footer>
      </section>
    </div>
  )
}
